import Image from "next/image";
import Link from "next/link";
import { useTrans } from "@/utils/useTrans";
import ComponentErrorBoundary from "@/v2-components/pages/ComponentErrorBoundary";
import { FeatureFlagName, useIsFeatureOn } from "@/queries/feature-flag-query";

function handleScrollToHowItWorks() {
  const target = document.getElementById("how-it-works");
  if (target) {
    const gnbHeight = window.innerWidth >= 1024 ? 70 : 64;
    const offset = gnbHeight + 16;
    const rect = target.getBoundingClientRect();
    window.scrollTo({
      top: rect.top + window.scrollY - offset,
      behavior: "smooth",
    });
  }
}

function LogoBadge({ size }: { size: "sm" | "lg" }) {
  const isLg = size === "lg";
  const IsLiveLaunching = useIsFeatureOn(FeatureFlagName.IsLiveLaunching);
  return (
    <div
      className={`inline-flex items-center rounded-full shadow-[inset_0_0_0_1px_rgba(255,255,255,0.4)] ${
        isLg ? "h-[60px] gap-[12px] px-[24px]" : "h-[40px] gap-[8px] px-[16px]"
      }`}
      style={{
        background:
          "linear-gradient(to right, #90c4f4, #ff77bb 50.481%, #ffcb95)",
      }}
    >
      {IsLiveLaunching ? (
        <>
          <svg
            viewBox="0 0 83 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={`shrink-0 ${isLg ? "h-[30px] w-[124px]" : "h-[20px] w-[83px]"}`}
            aria-hidden="true"
          >
            <path
              d="M67.6048 4.73633C67.0099 4.73633 66.5062 5.26013 66.5099 5.87814C66.5099 6.48485 67.0099 6.99735 67.6011 7.00111C68.2033 7.00111 68.7143 6.49239 68.7216 5.88191C68.7289 5.2639 68.2143 4.73633 67.6048 4.73633Z"
              fill="#121619"
            />
            <path
              d="M68.5099 7.50608H66.7252V14.8619H68.5099V7.50608Z"
              fill="#121619"
            />
            <path
              d="M36.3781 9.05113H40.1008V5.31291L41.8855 5.00014V14.8619H40.1008V10.8939H36.3781V14.8619H34.5934V5.31291L36.3781 5.00014V9.05113Z"
              fill="#121619"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M29.7466 7.41943C29.0678 7.41943 28.4291 7.61162 27.8853 7.947V5.00014L26.1006 5.31291V15.0089H27.8853V14.4248C28.4291 14.7602 29.0678 14.9524 29.7466 14.9524C31.7612 14.9524 33.3927 13.2679 33.3927 11.1878C33.3927 9.10766 31.7612 7.4232 29.7466 7.4232V7.41943ZM29.7466 13.1059C28.7174 13.1059 27.8853 12.2467 27.8853 11.184C27.8853 10.1213 28.7174 9.26216 29.7466 9.26216C30.7758 9.26216 31.608 10.1213 31.608 11.184C31.608 12.2467 30.7758 13.1059 29.7466 13.1059Z"
              fill="#121619"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M53.8345 7.947C54.3783 7.61161 55.017 7.41943 55.6958 7.41943C57.7105 7.41943 59.3419 9.10389 59.3419 11.184C59.3419 13.2642 57.7105 14.9486 55.6958 14.9486C55.017 14.9486 54.3783 14.7564 53.8345 14.421V17.3679H52.0498V7.3629H53.8345V7.947ZM53.8345 11.184C53.8345 12.2467 54.6666 13.1059 55.6958 13.1059C56.7251 13.1059 57.5572 12.2467 57.5572 11.184C57.5572 10.1213 56.7251 9.26216 55.6958 9.26216C54.6666 9.26216 53.8345 10.1213 53.8345 11.184Z"
              fill="#121619"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M48.5936 7.94699C48.0498 7.61161 47.4111 7.41942 46.7322 7.41942C44.7176 7.41942 43.0862 9.10388 43.0862 11.184C43.0862 13.2642 44.7176 14.9486 46.7322 14.9486C47.4111 14.9486 48.0498 14.7564 48.5936 14.421V15.0051H50.3783V7.35913H48.5936V7.94699ZM46.7322 13.1059C45.703 13.1059 44.8709 12.2467 44.8709 11.184C44.8709 10.1213 45.703 9.26215 46.7322 9.26215C47.7614 9.26215 48.5936 10.1213 48.5936 11.184C48.5936 12.2467 47.7614 13.1059 46.7322 13.1059Z"
              fill="#121619"
            />
            <path
              d="M74.9698 10.2231C74.645 9.65028 74.0464 9.26214 73.3566 9.26214V9.2546C72.6668 9.2546 72.0646 9.64274 71.7435 10.2155C71.5865 10.4982 71.4953 10.826 71.4953 11.1765C71.4953 11.5269 71.5865 11.8548 71.7435 12.1374C72.0683 12.7102 72.6668 13.0983 73.3566 13.0983C74.0464 13.0983 74.6486 12.7102 74.9698 12.1374L76.5173 13.0606C75.8859 14.1874 74.7107 14.9448 73.3603 14.9448C72.0099 14.9448 70.8347 14.1874 70.2033 13.0606C69.8931 12.5105 69.7142 11.8661 69.7142 11.1802C69.7142 10.4944 69.8931 9.85377 70.2033 9.29982C70.8347 8.17308 72.0099 7.41564 73.3603 7.41564C74.7107 7.41564 75.8859 8.17308 76.5173 9.29982L74.9698 10.2231Z"
              fill="#121619"
            />
            <path
              d="M81.9334 10.8825C81.6488 10.7431 81.3495 10.6301 81.0539 10.5283C80.8665 10.4627 80.6776 10.4038 80.4889 10.345C80.255 10.2721 80.0213 10.1993 79.7911 10.1138C79.583 10.0347 79.4079 9.88769 79.4224 9.61637C79.4334 9.3865 79.583 9.22069 79.8641 9.12648C80.3604 8.95691 80.9261 9.15663 81.2217 9.60507C81.2495 9.6442 81.2738 9.68513 81.3007 9.73037C81.3126 9.75049 81.3251 9.77145 81.3385 9.79349C81.8458 9.50709 82.3386 9.232 82.8532 8.9456L82.8374 8.91474C82.8139 8.86867 82.7983 8.83802 82.7802 8.80994C81.7473 7.10664 79.4298 7.12925 78.3348 8.0525C77.3458 8.88531 77.3129 10.4492 78.291 11.184C78.604 11.4174 78.9799 11.5686 79.3461 11.716L79.4006 11.738C79.6494 11.8371 79.9045 11.9199 80.1597 12.0028C80.3799 12.0743 80.6002 12.1458 80.8166 12.2278C81.021 12.3032 81.1779 12.4577 81.1597 12.7215C81.1414 12.9815 80.9809 13.1247 80.7582 13.1963C80.1524 13.396 79.2181 13.3094 78.875 12.4313C78.8704 12.4194 78.8643 12.4075 78.8568 12.3928C78.8538 12.387 78.8506 12.3806 78.8472 12.3737C78.8455 12.3705 78.8439 12.3671 78.8421 12.3635C78.6303 12.4857 78.4255 12.6043 78.2206 12.7228C77.9138 12.9005 77.6069 13.0781 77.2764 13.2679C77.4553 13.5618 77.6232 13.8181 77.8239 14.0404C78.8604 15.1936 81.0612 15.1709 82.1488 14.4022C83.2619 13.6146 83.3751 11.6023 81.9371 10.8825H81.9334Z"
              fill="#121619"
            />
            <path
              d="M63.4113 5.00014V7.5061H65.0536V9.34883H63.4113V12.6198C63.4113 12.8383 63.5828 13.0192 63.7945 13.0192H65.05V14.8619H63.2945C62.3748 14.8619 61.6266 14.0894 61.6266 13.1398V9.34883H59.9842V7.5061H61.6266V5.31291L63.4113 5.00014Z"
              fill="#121619"
            />
            <path
              d="M12.2349 19.3514L1.53127 10.8153C-0.896056 8.88037 -0.344058 4.9919 2.5256 3.83465L11.5184 0.214091C12.7467 -0.280806 14.1468 0.10246 14.9693 1.15551L21.2753 9.24876C22.1636 10.3874 22.138 12.006 21.2131 13.1149L16.3731 18.9234C15.3312 20.1737 13.5071 20.3598 12.2422 19.3514H12.2349ZM3.56745 8.56781L13.3279 16.3485C13.7483 16.6834 14.3588 16.6238 14.7061 16.2071L18.3946 11.7791C18.7017 11.4107 18.7127 10.8711 18.4166 10.4916L13.0794 3.64116C12.8052 3.29138 12.3373 3.16115 11.9278 3.32859L3.84893 6.5845C3.03373 6.91195 2.87654 8.02081 3.56745 8.57153V8.56781Z"
              fill="#121619"
            />
          </svg>
          <span
            className={`whitespace-nowrap font-[800] tracking-[0.25px] text-coolgray-100 ${
              isLg ? "text-[24px] leading-[30px]" : "text-[14px] leading-[18px]"
            }`}
          >
            LIVE
          </span>
        </>
      ) : (
        <span
          className={`whitespace-nowrap font-[800] tracking-[0.25px] text-coolgray-100 ${"text-[14px] leading-[18px]"}`}
        >
          Coming Soon, 2026. 05
        </span>
      )}
    </div>
  );
}

/* ── Desktop ─────────────────────────────────────────────────── */
function HeroDesktop() {
  const { t } = useTrans(["software"]);
  return (
    <section className="relative flex min-h-[640px] w-full items-center justify-center overflow-hidden bg-coolgray-100">
      <div className="absolute inset-0">
        <Image
          src="/images/software/live/hero-bg.jpg"
          alt=""
          fill
          className="object-cover"
          priority
        />
      </div>
      <div className="relative z-10 flex w-full flex-col items-center gap-[24px] px-4 py-0 text-center md:gap-[32px] lg:gap-[40px] lg:px-[120px] xl:px-[240px]">
        <LogoBadge size="sm" />

        <h1 className="text-center text-[36px] font-[800] leading-[48px] text-white md:text-[48px] md:leading-[64px] lg:text-[64px] lg:leading-[96px]">
          <p className="mb-0">
            {t("live_hero_title1_pre")}
            <span className="live-shake-a">
              {t("live_hero_title1_highlight")}
            </span>
            .
          </p>
          <p>
            <span className="bg-clip-text">
              {t("live_hero_title2_pre")}
              <span className="live-shake-b">
                {t("live_hero_title2_highlight")}
              </span>
              {t("live_hero_title2_suffix")}
            </span>
          </p>
        </h1>

        <div className="text-center text-[16px] font-400 text-white md:text-[18px] lg:text-[22px]">
          <p className="mb-0 leading-[24px] lg:leading-[28px]">
            {t("live_hero_desc_1")}
          </p>
          <p className="leading-[24px] lg:leading-[28px]">
            {t("live_hero_desc_2")}
          </p>
        </div>

        <div className="flex gap-[8px]">
          <Link
            href="/games/pc/software"
            className="flex h-[40px] items-center justify-center whitespace-nowrap rounded-[44px] bg-coolgray-90 px-[24px] text-[12px] font-600 leading-[16px] tracking-[0.1px] text-white"
          >
            {t("live_hero_cta_download")}
          </Link>
          <button
            onClick={handleScrollToHowItWorks}
            className="flex h-[40px] items-center justify-center whitespace-nowrap rounded-[44px] border border-white bg-black/20 px-[24px] text-[12px] font-600 leading-[16px] tracking-[0.1px] text-white"
          >
            {t("live_hero_cta_howto")}
          </button>
        </div>
      </div>
    </section>
  );
}

/* ── Mobile ──────────────────────────────────────────────────── */
function HeroMobile() {
  const { t } = useTrans(["software"]);
  return (
    <section className="flex w-full flex-col overflow-hidden">
      {/* 상단: 이미지 + 배지 */}
      <div className="relative h-[240px] w-full overflow-hidden">
        <Image
          src="/images/software/live/hero-bg-mobile.jpg"
          alt=""
          fill
          className="object-cover object-center"
          priority
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <LogoBadge size="lg" />
        </div>
      </div>

      {/* 하단: 텍스트 + 버튼 */}
      <div className="flex flex-col gap-[32px] bg-coolgray-10 px-[16px] py-[24px]">
        <div className="flex flex-col gap-[12px] text-center">
          <h1 className="text-[22px] font-400 leading-[28px] text-coolgray-90">
            <p className="mb-0">
              {t("live_hero_title1_pre")}
              <span className="live-shake-a">
                {t("live_hero_title1_highlight")}
              </span>
              .
            </p>
            <p>
              {t("live_hero_title2_pre")}
              <span className="live-shake-b">
                {t("live_hero_mobile_title2_highlight")}
              </span>
              {t("live_hero_mobile_title2_suffix")}
            </p>
          </h1>
          <p className="text-[14px] font-400 leading-[18px] tracking-[0.25px] text-coolgray-90">
            {t("live_hero_desc_1")}
            <br />
            {t("live_hero_desc_2")}
          </p>
        </div>

        <div className="flex w-full flex-col gap-[8px]">
          <Link
            href="/games/pc/software"
            className="flex h-[40px] w-full items-center justify-center rounded-[44px] bg-coolgray-90 text-[12px] font-600 leading-[16px] tracking-[0.1px] text-white"
          >
            {t("live_hero_cta_download")}
          </Link>
          <button
            onClick={handleScrollToHowItWorks}
            className="flex h-[40px] w-full items-center justify-center rounded-[44px] border border-coolgray-90 text-[12px] font-600 leading-[16px] tracking-[0.1px] text-coolgray-90"
          >
            {t("live_hero_cta_howto")}
          </button>
        </div>
      </div>
    </section>
  );
}

/* ── Export ──────────────────────────────────────────────────── */
export default function LiveHeroSection() {
  return (
    <ComponentErrorBoundary>
      <>
        <div className="hidden pb-[64px] md:block">
          <HeroDesktop />
        </div>
        <div className="pb-[64px] md:hidden">
          <HeroMobile />
        </div>
      </>
    </ComponentErrorBoundary>
  );
}
